import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import PrelaunchBookingsCard from './PrelaunchBookingsCard';
import {
  Rocket, RefreshCw, Check, X, CircleDashed, Info, Square, CheckSquare,
} from 'lucide-react';

const PHASE_ACCENT = {
  practitioner: { bar: 'bg-teal-400',   text: 'text-teal-300',   ring: 'border-teal-500/40' },
  vendor:       { bar: 'bg-amber-400',  text: 'text-amber-300',  ring: 'border-amber-500/40' },
  public:       { bar: 'bg-violet-400', text: 'text-violet-300', ring: 'border-violet-500/40' },
};

const Tick = ({ ok }) => ok
  ? <Check className="w-4 h-4 text-emerald-400" strokeWidth={3} />
  : <X className="w-4 h-4 text-gray-600" strokeWidth={3} />;

function PhaseCard({ phase }) {
  const a = PHASE_ACCENT[phase.key];
  const late = phase.days_remaining < 0;
  return (
    <Card className={`bg-gray-900/60 border ${a.ring}`} data-testid={`launch-phase-${phase.key}`}>
      <CardContent className="p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-base font-semibold text-white">{phase.name}</h3>
          <span className={`text-xs font-medium ${late ? 'text-red-400' : a.text}`}>
            {late ? `${Math.abs(phase.days_remaining)}d overdue` : `${phase.days_remaining} days`}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {new Date(phase.date + 'T12:00:00').toLocaleDateString(undefined, {
            month: 'long', day: 'numeric', year: 'numeric',
          })}
        </p>

        <div className="flex items-end justify-between mt-4 mb-2">
          <span className={`text-3xl font-bold ${a.text}`}>{phase.percent}%</span>
          <span className="text-xs text-gray-500">{phase.done} of {phase.total} ready</span>
        </div>
        <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
          <div
            className={`h-full ${a.bar} transition-all duration-700`}
            style={{ width: `${phase.percent}%` }}
          />
        </div>

        {phase.blockers.length > 0 && (
          <p className="text-xs text-gray-400 mt-3 leading-relaxed">
            <span className="text-gray-500">Still open: </span>
            {phase.blockers.slice(0, 3).join(' · ')}
            {phase.blockers.length > 3 && ` · +${phase.blockers.length - 3} more`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminLaunchTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/launch/readiness`, { headers });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    setData(d => ({
      ...d,
      manual_items: d.manual_items.map(m => m.key === item.key ? { ...m, done: !m.done } : m),
    }));
    try {
      await axios.put(`${API}/admin/launch/checklist/${item.key}`, { done: !item.done }, { headers });
      load();
    } catch {
      load();
    }
  };

  if (loading && !data) return <p className="text-sm text-gray-400">Loading launch readiness…</p>;
  if (!data) return <p className="text-sm text-red-400">Could not load launch readiness.</p>;

  const env = data.environment;

  return (
    <div className="space-y-6" data-testid="admin-launch-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Rocket className="w-5 h-5 text-teal-400" /> Launch readiness
          </h2>
          <p className="text-sm text-gray-400 mt-1 max-w-3xl">
            Everything below is checked against live data each time this loads, so it cannot drift.
            Items only you can confirm are ticked by hand.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}
                className="bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:text-white"
                data-testid="launch-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <PrelaunchBookingsCard />

      {/* How data moves from here to production */}
      <div className="p-4 rounded-lg bg-teal-900/20 border border-teal-700/40" data-testid="launch-preview-warning">
        <p className="text-sm font-semibold text-teal-200 flex items-center gap-2">
          <Info className="w-4 h-4" /> Your first Publish carries this data to production
        </p>
        <p className="text-xs text-teal-200/80 mt-1 leading-relaxed">
          Everything entered here — signed agreements, profiles, uploaded documents — is copied to the
          live database on the <strong>first</strong> Publish. So practitioners and vendors can be
          onboarded now, before launch. After that first push the two databases are independent: you keep
          testing here without touching real users, and later deploys never overwrite production data.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {data.phases.map(p => <PhaseCard key={p.key} phase={p} />)}
      </div>

      {/* Environment */}
      <Card className="bg-gray-900/60 border-gray-700">
        <CardContent className="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Live integrations</p>
          <div className="flex flex-wrap gap-2" data-testid="launch-env-badges">
            <Badge className={env.stripe_mode === 'live'
              ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50'
              : 'bg-amber-900/30 text-amber-200 border-amber-700/50'}>
              Stripe: {env.stripe_mode.toUpperCase()}
            </Badge>
            <Badge className={env.easypost_mode === 'live'
              ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50'
              : 'bg-amber-900/30 text-amber-200 border-amber-700/50'}>
              EasyPost: {env.easypost_mode.toUpperCase()}
            </Badge>
            <Badge className={env.tax_enabled
              ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50'
              : 'bg-gray-800 text-gray-300 border-gray-600'}>
              Sales tax: {env.tax_enabled ? 'ON' : 'OFF'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Per-phase checklists */}
      {data.phases.map(phase => {
        const auto = data.auto_items.filter(i => i.phase === phase.key);
        const manual = data.manual_items.filter(i => i.phase === phase.key);
        const a = PHASE_ACCENT[phase.key];
        return (
          <Card key={phase.key} className="bg-gray-900/60 border-gray-700">
            <CardContent className="p-5">
              <h3 className={`text-base font-semibold ${a.text} mb-4`}>
                {phase.name} — {new Date(phase.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </h3>

              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Checked automatically</p>
              <div className="space-y-1.5 mb-5">
                {auto.map(i => (
                  <div key={i.key} className="flex items-start gap-3 py-1.5 border-b border-gray-800/70 last:border-0"
                       data-testid={`launch-auto-${i.key}`}>
                    <span className="mt-0.5">{i.done
                      ? <Check className="w-4 h-4 text-emerald-400" strokeWidth={3} />
                      : <CircleDashed className="w-4 h-4 text-gray-500" />}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${i.done ? 'text-gray-400' : 'text-gray-200'}`}>{i.label}</p>
                      <p className="text-xs text-gray-500">{i.status}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Your confirmations</p>
              <div className="space-y-1.5">
                {manual.map(i => (
                  <button
                    key={i.key}
                    onClick={() => toggle(i)}
                    className="w-full flex items-start gap-3 py-2 text-left border-b border-gray-800/70 last:border-0 hover:bg-gray-800/30 rounded transition-colors"
                    data-testid={`launch-manual-${i.key}`}
                  >
                    <span className="mt-0.5">{i.done
                      ? <CheckSquare className="w-4 h-4 text-emerald-400" />
                      : <Square className="w-4 h-4 text-gray-500" />}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${i.done ? 'text-gray-400 line-through' : 'text-gray-200'}`}>{i.label}</p>
                      <p className="text-xs text-gray-500 leading-relaxed">{i.detail}</p>
                      {i.done && i.completed_by && (
                        <p className="text-[11px] text-emerald-500/80 mt-0.5">
                          Confirmed by {i.completed_by}
                          {i.completed_at ? ` on ${new Date(i.completed_at).toLocaleDateString()}` : ''}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Practitioner roster */}
      <Card className="bg-gray-900/60 border-gray-700">
        <CardContent className="p-5">
          <h3 className="text-base font-semibold text-white mb-1">
            Practitioner onboarding — {data.practitioners.length} people
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Least-ready first. Contributor role is required before anyone can create a workshop, and
            nobody can be paid until Stripe verification is finished — that one is on them, not you.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="launch-practitioner-table">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-700">
                  <th className="text-left py-2 font-medium">Person</th>
                  <th className="py-2 font-medium">Agreement</th>
                  <th className="py-2 font-medium">Profile</th>
                  <th className="py-2 font-medium">Contributor</th>
                  <th className="py-2 font-medium">Placard</th>
                  <th className="py-2 font-medium">Payouts</th>
                </tr>
              </thead>
              <tbody>
                {data.practitioners.map(p => (
                  <tr key={p.user_id} className="border-b border-gray-800/70">
                    <td className="py-2.5">
                      <p className="text-gray-200">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.email}</p>
                    </td>
                    {['agreement', 'profile', 'contributor', 'placard', 'stripe_connect'].map(k => (
                      <td key={k} className="py-2.5 text-center"><div className="flex justify-center"><Tick ok={p[k]} /></div></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Vendor roster */}
      <Card className="bg-gray-900/60 border-gray-700">
        <CardContent className="p-5">
          <h3 className="text-base font-semibold text-white mb-1">
            Storefront readiness — {data.vendors.length} storefronts
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            A storefront can only sell with a return policy, a ship-from address, verified
            payouts and at least one published product.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="launch-vendor-table">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-700">
                  <th className="text-left py-2 font-medium">Storefront</th>
                  <th className="text-left py-2 font-medium">Status</th>
                  <th className="py-2 font-medium">Returns</th>
                  <th className="py-2 font-medium">Ship from</th>
                  <th className="py-2 font-medium">Payouts</th>
                  <th className="py-2 font-medium">Products</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.map(v => (
                  <tr key={v.vendor_id} className="border-b border-gray-800/70">
                    <td className="py-2.5 text-gray-200">{v.store_name}</td>
                    <td className="py-2.5">
                      <Badge className={v.status === 'approved'
                        ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50'
                        : v.status === 'pending'
                          ? 'bg-amber-900/30 text-amber-200 border-amber-700/50'
                          : 'bg-gray-800 text-gray-300 border-gray-600'}>
                        {v.status}
                      </Badge>
                    </td>
                    {['return_policy', 'ship_from', 'stripe_connect'].map(k => (
                      <td key={k} className="py-2.5 text-center"><div className="flex justify-center"><Tick ok={v[k]} /></div></td>
                    ))}
                    <td className="py-2.5 text-center text-gray-300">{v.products}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
